// Client-side typed fetcher for /api/airport-nasr (truthy NASR airport
// + per-end runway data). Used by RunwayTab to render end-elev,
// displaced thresholds, ILS, REIL/PAPI/VASI, LAHSO, etc.
//
// Returns null when:
//   * the dataset hasn't been imported yet (503 from the server)
//   * the ident isn't in NASR (404 \u2014 e.g. non-US airports)
//
// Callers can fall back to the OurAirports-backed /api/airport for
// the basics in those cases.

export interface NasrIls {
  type: string;
  magneticCourseDeg: number | null;
  localizerMhz: number | null;
  category: string;
  ilsMagVar: number | null;
}

export interface NasrRunwayEnd {
  ident: string;
  trueAlignmentDeg: number | null;
  ilsType: string;
  rightTrafficPattern: boolean;
  endLat: number | null;
  endLon: number | null;
  endElevFt: number | null;
  thresholdCrossingHeightFt: number | null;
  glidePathDeg: number | null;
  displacedThresholdLat: number | null;
  displacedThresholdLon: number | null;
  displacedThresholdElevFt: number | null;
  displacedThresholdLengthFt: number | null;
  touchdownZoneElevFt: number | null;
  vgsi: string;
  approachLights: string;
  reil: boolean;
  centerlineLights: boolean;
  touchdownLights: boolean;
  toraFt: number | null;
  todaFt: number | null;
  asdaFt: number | null;
  ldaFt: number | null;
  lahsoLdaFt: number | null;
  lahsoIntersection: string;
  ils?: NasrIls | null;
}

export interface NasrRunway {
  ident: string;
  lengthFt: number;
  widthFt: number;
  surface: string;
  edgeLighting: string;
  baseEnd: NasrRunwayEnd | null;
  recipEnd: NasrRunwayEnd | null;
}

export interface NasrAirportDetail {
  ident: string;
  faaIdent: string;
  icaoIdent: string;
  facilityType?: string;
  name: string;
  city: string;
  state: string;
  lat: number;
  lon: number;
  elevFt: number | null;
  magVar: number | null;
  runways: NasrRunway[];
}

export interface NasrAirportResponse {
  cycle: string;
  airport: NasrAirportDetail;
}

export async function fetchNasrAirport(
  ident: string,
  signal?: AbortSignal,
): Promise<NasrAirportResponse | null> {
  const r = await fetch(`/api/airport-nasr?ident=${encodeURIComponent(ident)}`, { signal });
  if (r.status === 404 || r.status === 503) return null;
  if (!r.ok) throw new Error(`NASR airport fetch HTTP ${r.status}`);
  return (await r.json()) as NasrAirportResponse;
}
